//////////////
// IMPORTS
/////////////

// Ensure init-env is imported first to make sure public path and environment
// overrides are set before importing any other constants
import "@clockwise/web-commons/src/config/init-env";
// To avoid import autosort moving the init-env import, add other imports below this comment

// libraries
import * as Sentry from "@sentry/browser";
import * as React from "react";
import { Store } from "redux";
// util
import { getSession } from "./util/auth.util";
import { getHealthAugmented, loadDowntime, setupDowntimeInterval } from "./util/health.util";
import { Monitor } from "./util/monitoring.util";
import { createPolyfills } from "./util/polyfill.util";
import { PostMessageManager } from "./util/post-message.util";
// internal
import { getEnvironment } from "@clockwise/client-commons/src/config/environment";
import { IReduxState } from "./state/reducers/root.reducer";
import { initReduxStore } from "./state/redux-store";
import { createReduxStoreModern } from "./state/redux-store.modern";
import { createEnvironment } from "./state/relay-environment";
// top-level css imports
import "./assets/css/chrome.css";
import "./assets/css/index.css";

import { awaitOfficeDefined } from "@clockwise/web-commons/src/util/office.util";
import { createRoot } from "react-dom/client";
import { RootOutlook } from "./components/root";
import { outlookRouteConfig } from "./routing/routes-outlook";
import { logger } from "./util/logger.util";

async function loadApp(store: Store<IReduxState>) {
  // create the relay modern environment
  createEnvironment(store);

  // Wait for Office to be present on window
  await awaitOfficeDefined();

  window.Office.onReady()
    .then((res) => {
      const isOutlook = res.host === window.Office.HostType.Outlook;

      const rootElem = document.getElementById("root");
      if (!rootElem) {
        throw new Error("Root element not found");
      }
      createRoot(rootElem).render(<RootOutlook reduxStore={store} isOutlook={isOutlook} />);
    })
    .catch((err) => {
      logger.error("Error loading Office. Webapp not loading. P0.", err);
    });
}

async function main() {
  // creates the redux store for Chrome - important to do this right away
  // we also use this as a flag to determine if we are initialized or not
  const modernReduxStore = await createReduxStoreModern(outlookRouteConfig);
  initReduxStore(modernReduxStore);

  // first, we need to polyfill
  createPolyfills();

  // init Sentry
  if (getEnvironment() === "production") {
    Sentry.init({
      dsn: "https://f04d9c7a34e745548033e1b04b37bfc0@sentry.io/164680",
      release: process.env.RELEASE,
      environment: getEnvironment(),
    });
  }

  //////////////
  // APP
  /////////////
  // init the post message manager immediately so we can do proper analytics tracking
  PostMessageManager.init(modernReduxStore);

  const monitor = new Monitor("modern");
  monitor.start();

  // fetch the session
  await getSession(modernReduxStore);

  loadApp(modernReduxStore);

  // fetch the health, start refetch interval, and load downtime if needed
  const healthPayload = await getHealthAugmented();
  setupDowntimeInterval(healthPayload);
  if (healthPayload.health === "dead") {
    return loadDowntime(healthPayload);
  }
}

void main();
